import { sql } from "drizzle-orm";
import {
  bigint,
  boolean,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";

export const user = pgTable("user", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  emailVerified: boolean("email_verified").default(false).notNull(),
  image: text("image"),
  /** Platform admin: may act on any tenant via the x-loot-tenant header. */
  isPlatformAdmin: boolean("is_platform_admin").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at")
    .defaultNow()
    .$onUpdate(() => /* @__PURE__ */ new Date())
    .notNull(),
});

export const session = pgTable(
  "session",
  {
    id: text("id").primaryKey(),
    expiresAt: timestamp("expires_at").notNull(),
    token: text("token").notNull().unique(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    activeTenantId: text("active_tenant_id"),
  },
  (table) => [index("session_userId_idx").on(table.userId)],
);

export const account = pgTable(
  "account",
  {
    id: text("id").primaryKey(),
    accountId: text("account_id").notNull(),
    providerId: text("provider_id").notNull(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    accessToken: text("access_token"),
    refreshToken: text("refresh_token"),
    idToken: text("id_token"),
    accessTokenExpiresAt: timestamp("access_token_expires_at"),
    refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
    scope: text("scope"),
    password: text("password"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("account_userId_idx").on(table.userId)],
);

export const verification = pgTable(
  "verification",
  {
    id: text("id").primaryKey(),
    identifier: text("identifier").notNull(),
    value: text("value").notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("verification_identifier_idx").on(table.identifier)],
);

/**
 * Better Auth's rate-limit counters (`RATE_LIMIT_STORAGE` in auth/auth.ts), one row per client IP
 * and auth path, so every dashboard API container counts against the same limit. Distinct from the
 * public API's `rate_limit_windows`. Writer: Better Auth itself. Not tenant-scoped, and never
 * returned by any API.
 */
export const betterAuthRateLimit = pgTable("better_auth_rate_limit", {
  id: text("id").primaryKey(),
  /** `{ip}|{path}`, as Better Auth's `createRateLimitKey` builds it. */
  key: text("key").notNull().unique(),
  count: integer("count").notNull(),
  /** Epoch milliseconds of the last counted request. */
  lastRequest: bigint("last_request", { mode: "number" }).notNull(),
});

/**
 * One tenant (= Better Auth's "organization", see TENANT_PLUGIN_SCHEMA). Writer: platform staff
 * (platform.tenants.*); name/slug/logo also through Better Auth's organization plugin.
 * archived_at / archived_by are written only by platform.tenants.archive and restore.
 */
export const tenant = pgTable(
  "tenant",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull(),
    slug: text("slug").notNull().unique(),
    logo: text("logo"),
    createdAt: timestamp("created_at").notNull(),
    metadata: text("metadata"),
    /**
     * Set when platform staff archive the tenant: its storefronts 404, its API keys (and the
     * shopper sessions they minted) stop authenticating, members lose the dashboard, and
     * tenant-scoped mutations are refused even for platform admins. Null = active. Reversible.
     */
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    /** user.id of the platform admin who archived it. Provenance only, no FK (cf. end_users.locked_by). */
    archivedBy: text("archived_by"),
    /**
     * The `{name}.myshopify.com` whose App Store install created this tenant
     * (`provisionShopTenant`); null for a tenant staff created. Provenance, no FK: it outlives the
     * shop row, and `shop/redact` archives the tenant it names.
     */
    createdViaShopDomain: text("created_via_shop_domain"),
  },
  (table) => [uniqueIndex("tenant_slug_uidx").on(table.slug)],
);

export const member = pgTable(
  "member",
  {
    id: text("id").primaryKey(),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    /** Tenant role: viewer | editor | admin (owner = hidden superset of admin). */
    role: text("role").default("viewer").notNull(),
    createdAt: timestamp("created_at").notNull(),
  },
  (table) => [
    index("member_tenant_idx").on(table.tenantId),
    index("member_userId_idx").on(table.userId),
    uniqueIndex("member_tenant_user_uidx").on(table.tenantId, table.userId),
  ],
);

export const invitation = pgTable(
  "invitation",
  {
    id: text("id").primaryKey(),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    email: text("email").notNull(),
    role: text("role"),
    status: text("status").default("pending").notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    inviterId: text("inviter_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
  },
  (table) => [
    index("invitation_tenant_idx").on(table.tenantId),
    index("invitation_email_idx").on(table.email),
  ],
);

export const PLATFORM_ADMIN_INVITATION_STATUSES = ["pending", "accepted", "canceled"] as const;
export type PlatformAdminInvitationStatus = (typeof PLATFORM_ADMIN_INVITATION_STATUSES)[number];

/**
 * An invitation to become a platform admin, for an email with no dashboard account yet. The id
 * is the link (unguessable ULID, like `invitation`): whoever opens /accept-admin-invite?id=… sets
 * their own password and the account is created then, already a platform admin.
 *
 * Writer: platform staff (platform.admins.grant creates and cancels, platform.admins.cancelInvite);
 * the invitee flips `status` to accepted (platformInvites.accept). Platform-only: never part of
 * a tenant-facing response.
 */
export const platformAdminInvitation = pgTable(
  "platform_admin_invitation",
  {
    id: text("id").primaryKey(),
    /** Lowercased; the account is created under exactly this address. */
    email: text("email").notNull(),
    status: text("status", { enum: PLATFORM_ADMIN_INVITATION_STATUSES })
      .default("pending")
      .notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    acceptedAt: timestamp("accepted_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    inviterId: text("inviter_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
  },
  (table) => [
    index("platform_admin_invitation_email_idx").on(table.email),
    // One live invitation per address: a re-grant cancels the old one first.
    uniqueIndex("platform_admin_invitation_pending_email_uidx")
      .on(table.email)
      .where(sql`${table.status} = 'pending'`),
  ],
);
